import { useMutation } from '@apollo/client';
import { message } from 'antd';
import { useTranslation } from 'react-i18next';
import {
    SUBMIT_ORDER_ADDRESS_LOCATION_CHANGE,
    SubmitOrderAddressLocationChangeResponse,
    SubmitOrderAddressLocationChangeVariables,
} from '../pages/delivery/parcels/graphql/mutations/submitOrderAddressLocationChange';

const ERROR_CODE_I18N_KEYS: Record<string, string> = {
    NOT_IN_GRAY: 'delivery.parcels.error.addressLocationNotInGray',
    ORDER_FINISHED: 'delivery.parcels.error.addressLocationOrderFinished',
    PENDING_EXISTS: 'delivery.parcels.error.addressLocationPendingExists',
    INVALID_COORDINATE: 'delivery.parcels.error.addressLocationInvalidCoordinate',
    SAME_AS_CURRENT: 'delivery.parcels.error.sameGeoLocationMessage',
    NO_RECIPIENT_ADDRESS: 'delivery.parcels.error.addressLocationNoRecipientAddress',
    NO_CURRENT_COORDINATE: 'delivery.parcels.error.addressLocationNoCurrentCoordinate',
    ORDER_NOT_FOUND: 'delivery.parcels.error.addressLocationOrderNotFound',
};

interface UseSubmitOrderAddressLocationChangeParams {
    orderId?: string;
    refetch: () => Promise<unknown>;
}

export const useSubmitOrderAddressLocationChange = ({
    orderId,
    refetch,
}: UseSubmitOrderAddressLocationChangeParams) => {
    const { t } = useTranslation();
    const [submitMutation, { loading }] = useMutation<
        SubmitOrderAddressLocationChangeResponse,
        SubmitOrderAddressLocationChangeVariables
    >(SUBMIT_ORDER_ADDRESS_LOCATION_CHANGE);

    const submitLocation = async (latitude: number, longitude: number) => {
        if (!orderId) {
            message.error(t('delivery.parcels.message.orderIdEmpty'));
            return;
        }

        try {
            const result = await submitMutation({
                variables: {
                    orderId,
                    latitude,
                    longitude,
                },
            });
            const payload = result.data?.submitOrderAddressLocationChange;
            if (payload?.success) {
                message.success(t('delivery.parcels.message.addressLocationSubmitSuccess'));
                await refetch();
                return;
            }

            const code = payload?.code ?? '';
            const i18nKey = ERROR_CODE_I18N_KEYS[code];
            const errorText = i18nKey
                ? t(i18nKey)
                : (payload?.message || t('delivery.parcels.message.addressLocationSubmitFailed'));
            message.error(errorText);
        } catch (error) {
            console.error('Submit address location change failed:', error);
            message.error(t('delivery.parcels.message.addressLocationSubmitFailed'));
        }
    };

    return {
        submitLocation,
        submittingLocation: loading,
    };
};
